// CartIQ 360 — Mobile Auth Bridge (React route)
//
// Receives users back from Google OAuth and hands them off to the mobile app
// via a deep link. The deep-link target is now DYNAMIC — mobile passes its
// own return URL as a `?return=` query param when launching the OAuth flow,
// so this same page works for:
//
//   • Standalone dev/prod build  → cartiq360://auth-callback
//   • Expo Go (LAN)              → exp://192.168.x.x:8081/--/auth-callback
//   • Expo Go (tunnel)           → exp://u.expo.dev/<project>/--/auth-callback
//
// Mobile builds the return URL with `Linking.createURL('auth-callback')` and
// URL-encodes it into `?return=…` on the redirect URL it hands to Emergent.
// Emergent appends `#session_id=…` to the fragment AFTER the query, so the
// final URL the user lands on looks like:
//
//   https://cartiq360.in/auth/mobile-bridge?return=<encoded>#session_id=XYZ
//
// SECURITY: we only honor `return` values whose scheme is in
// ALLOWED_SCHEMES below. This closes the open-redirect hole that would
// otherwise let an attacker craft a phishing URL pointing at any deep link.

import React, { useEffect, useRef, useState } from "react";

const ALLOWED_SCHEMES = ["cartiq360://", "exp://"];
const DEFAULT_FALLBACK = "cartiq360://auth-callback";

function pickReturnUrl(rawReturn) {
  if (!rawReturn || typeof rawReturn !== "string") return DEFAULT_FALLBACK;
  let decoded = rawReturn;
  try {
    decoded = decodeURIComponent(rawReturn);
  } catch {
    // already decoded or malformed — fall through with original string
  }
  const ok = ALLOWED_SCHEMES.some((s) => decoded.startsWith(s));
  return ok ? decoded : DEFAULT_FALLBACK;
}

export default function MobileAuthBridge() {
  const [showManual, setShowManual] = useState(false);
  const [deepLink, setDeepLink] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const triggered = useRef(false);

  useEffect(() => {
    if (triggered.current) return;
    triggered.current = true;

    try {
      const rawHash = window.location.hash || "";
      const fragment = rawHash.startsWith("#") ? rawHash.slice(1) : rawHash;
      if (!fragment) {
        setErrorMsg(
          "Missing session. Please return to the app and try again."
        );
        return;
      }

      const params = new URLSearchParams(window.location.search || "");
      const returnUrl = pickReturnUrl(params.get("return"));

      // If `return` already has a fragment (rare — but Expo Go tunnel URLs
      // can carry a trailing `--/<path>` segment), we still append our own.
      // Two `#` in a URL collapses to one anchor, so we strip any existing
      // fragment on the return URL before appending the session_id.
      const cleanedReturn = returnUrl.split("#")[0];
      const target = `${cleanedReturn}#${fragment}`;
      setDeepLink(target);

      // 1) Best-effort programmatic handoff. iOS/Android are most cooperative
      //    when this happens immediately on first paint.
      window.location.replace(target);

      // 2) Surface a manual fallback button after a short grace period in
      //    case the OS blocked the programmatic scheme handoff.
      const t = setTimeout(() => setShowManual(true), 1500);
      return () => clearTimeout(t);
    } catch {
      setErrorMsg("Sign-in error. Please return to the app and try again.");
    }
  }, []);

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0A0F",
        color: "#F0EDE8",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, Manrope, 'Segoe UI', sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        gap: 18,
      }}
      data-testid="mobile-auth-bridge"
    >
      {!errorMsg && (
        <div
          aria-hidden
          style={{
            width: 44,
            height: 44,
            border: "3px solid rgba(255,107,0,0.18)",
            borderTopColor: "#FF6B00",
            borderRadius: "50%",
            animation: "cartiqSpin 720ms linear infinite",
          }}
        />
      )}
      <div
        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.3px" }}
        data-testid="mobile-auth-bridge-title"
      >
        {errorMsg ? "Sign-in error" : "Returning to CartIQ\u2026"}
      </div>
      <div
        style={{
          color: errorMsg ? "#E24B4A" : "#888",
          fontSize: 14,
          textAlign: "center",
          maxWidth: 320,
        }}
      >
        {errorMsg ||
          (showManual
            ? "If the app didn\u2019t open, tap below."
            : "If the app doesn\u2019t open automatically, tap below.")}
      </div>
      {showManual && !errorMsg && deepLink && (
        <a
          href={deepLink}
          data-testid="mobile-auth-bridge-open-app"
          style={{
            display: "inline-block",
            marginTop: 12,
            padding: "12px 22px",
            background: "#FF6B00",
            color: "#fff",
            border: 0,
            borderRadius: 8,
            fontWeight: 800,
            letterSpacing: 1,
            textDecoration: "none",
            cursor: "pointer",
            fontSize: 13,
          }}
        >
          OPEN CARTIQ APP
        </a>
      )}
      <style>{`@keyframes cartiqSpin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
